import { useCallback, useMemo } from 'react';

import { useSession } from '@ais-ui/websockets';
import { useQueryClient, UseQueryResult } from '@tanstack/react-query';

import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';

import { useCredentials } from '@ais-ui/auth';
import { useBasePath, REFETCH_INTERVALS } from '@ais-ui/custom-hooks';
import {
  Configuration,
  ShipTrackIdGetRequest,
  TrackApi,
  TrackPost,
  type TrackResult,
} from '@ais-ui/api-spec/portserver';
import { getBasePath } from './getAISBasePath';
import { getAuthHeaderFromCredentials } from '@ais-ui/utils';
import { useApiClientWrapper } from './apiClientWrapper';

// Credentials type for direct injection
export interface TracksApiCredentials {
  username: string;
  password: string;
}

// Core function to create TrackApi - can be used with or without hooks
export const createTracksApi = (
  sessionId: string,
  hostname: string,
  credentials?: TracksApiCredentials | null,
): TrackApi => {
  let basePath = getBasePath();
  let configuration: Configuration;

  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);
    configuration = new Configuration({
      basePath: `${basePath}api/portserver`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}aisweb/api/portserver`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  return new TrackApi(configuration);
};

// Hook version that uses context (for main app)
const useTracksApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { hostname } = useBasePath();
  const apiClientWrapper = useApiClientWrapper();

  const tracksApi = useMemo(
    () => createTracksApi(sessionId, hostname, credentials),
    [sessionId, hostname, credentials],
  );
  return apiClientWrapper(tracksApi) as TrackApi;
};

export const useTracksGetList = (
  enabled = true,
): UseQueryResult<{
  records?: Array<TrackResult>;
}> => {
  const tracksApi = useTracksApi();

  return useQuery(
    {
      queryKey: ['trackListGet'],
      queryFn: async () => {
        return await tracksApi.trackGet();
      },
      enabled: enabled,
      refetchInterval: REFETCH_INTERVALS.STANDARD,
      refetchIntervalInBackground: true, // Continue polling even when tab is not active
    },
    'tracks:list',
  );
};

export const useCreateTrack = () => {
  const tracksApi = useTracksApi();
  const queryClient = useQueryClient();

  return useMutation(
    {
      mutationFn: (route: TrackPost) => {
        return tracksApi.shipTrackPost({ json: route });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['trackListGet'] });
        queryClient.invalidateQueries({ queryKey: ['arrivals'] });
      },
    },
    'track:create',
  );
};

export const useUpdateTrack = () => {
  const tracksApi = useTracksApi();
  const queryClient = useQueryClient();

  return useMutation(
    {
      mutationFn: ({ id, route }: { id: number; route: TrackPost }) => {
        return tracksApi.shipTrackIdPut({ id, json: route });
      },
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ['trackListGet'] }),
    },
    'track:update',
  );
};

export const useDeleteTrack = () => {
  const tracksApi = useTracksApi();
  const queryClient = useQueryClient();

  return useMutation(
    {
      mutationFn: (id: number) => {
        return tracksApi.shipTrackIdDelete({ id });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['trackListGet'] });
        queryClient.invalidateQueries({ queryKey: ['arrivals'] });
      },
    },
    'track:delete',
  );
};

export const useFetchTrackById = () => {
  const tracksApi = useTracksApi();

  return useCallback(
    async (requestParameters: ShipTrackIdGetRequest) => {
      return await tracksApi.shipTrackIdGet(requestParameters);
    },
    [tracksApi],
  );
};
